/**
 * web/src/components/layout/RoleGuard.tsx
 * Client-side role-based render filter. NOT a security boundary — the
 * server's requireRole middleware (src/auth/middleware.ts lines 63-72) is
 * authoritative. RoleGuard merely hides controls that would 403 anyway,
 * so the UI matches what the user can actually do.
 *
 * Maps to src/auth/middleware.ts requireRole semantics: render only if
 * me.role ∈ allowed roles; otherwise render fallback (default null).
 *
 * Threat T-05-05-01 (Elevation of privilege): a user who manually crafts
 * a request still hits the server's 403. RoleGuard is purely visual.
 */
import type { ReactNode } from 'react';
import { useAuth } from '../../hooks/useAuth';
import type { Role } from '../../api/types';

/** Public props for RoleGuard. */
export interface RoleGuardProps {
  /** Allowed roles — if me.role is in this list, children render. */
  roles: Role[];
  /** Children rendered when role is allowed. */
  children: ReactNode;
  /** Optional fallback rendered when role is NOT allowed (default: null). */
  fallback?: ReactNode;
}

/**
 * Conditionally render `children` based on the authenticated user's role.
 * - If `me` is undefined (still loading or unauthenticated), render null —
 *   AppShell handles the unauthenticated-redirect; RoleGuard never flashes
 *   a "no access" fallback during the auth bootstrap.
 * - If `me.role` is in `roles`, render `children`.
 * - Otherwise render `fallback` (null by default).
 *
 * @sideEffects calls useAuth (subscribes to /api/me React Query cache)
 * @idempotent yes — multiple calls share the underlying useMe cache entry
 */
export function RoleGuard({ roles, children, fallback = null }: RoleGuardProps) {
  const { me } = useAuth();
  if (!me) return null;
  if (!roles.includes(me.role)) return <>{fallback}</>;
  return <>{children}</>;
}
